import React, { useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { CardBrandIcon } from "./CardBrandIcons";

export type CheckoutPaymentChoice = "ach" | "saved_card" | "one_off";

const PAYMENT_CONTROL_HEIGHT = 48;

type SavedMethodSummary = {
  bankName?: string | null;
  last4?: string | null;
} | null;

type CheckoutPaymentMethodBoxProps = {
  achAccount: SavedMethodSummary;
  cardAccount: SavedMethodSummary;
  cardPaymentEnabled: boolean;
  paymentChoice: CheckoutPaymentChoice;
  onPaymentChoiceChange: (choice: CheckoutPaymentChoice) => void;
  action?: React.ReactNode;
};

const formatAchDetail = (achAccount: SavedMethodSummary) => {
  const last4 = achAccount?.last4?.trim();
  return last4 ? `ACH ****${last4}` : "ACH";
};

const formatCardBrand = (cardAccount: SavedMethodSummary) => {
  const brand = cardAccount?.bankName?.trim();
  if (!brand) {
    return "Card";
  }
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

const formatCardDetail = (cardAccount: SavedMethodSummary) => {
  const brand = formatCardBrand(cardAccount);
  const last4 = cardAccount?.last4?.trim();
  return last4 ? `${brand} ****${last4}` : brand;
};

const ICON_SLOT_WIDTH = 36;

const IconSlot: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      width: ICON_SLOT_WIDTH,
      minWidth: ICON_SLOT_WIDTH,
      height: 24,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </Box>
);

const OptionRow: React.FunctionComponent<{
  icon: React.ReactNode;
  title: string;
  detail?: string;
}> = ({ icon, title, detail }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 0.5, minWidth: 0 }}>
    <IconSlot>{icon}</IconSlot>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {title}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mt: 0.25 }}>
          {detail}
        </Typography>
      )}
    </Box>
  </Box>
);

const PaymentTypeDivider: React.FunctionComponent = () => (
  <Box
    aria-hidden
    sx={{
      width: "1px",
      height: 14,
      bgcolor: "grey.300",
      flexShrink: 0,
    }}
  />
);

const PaymentTypeLabel: React.FunctionComponent<{
  icon: React.ReactNode;
  label: string;
}> = ({ icon, label }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
    {icon}
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.2 }}
    >
      {label}
    </Typography>
  </Box>
);

const PaymentSectionDivider: React.FunctionComponent = () => (
  <Box
    aria-hidden
    sx={{
      width: "100%",
      height: "1px",
      bgcolor: "grey.300",
      mb: 1.25,
    }}
  />
);

const PaymentMethodTypesRow: React.FunctionComponent<{ cardPaymentEnabled: boolean }> = ({
  cardPaymentEnabled,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 1,
      mb: 1,
    }}
  >
    <PaymentTypeLabel
      icon={<AccountBalanceIcon sx={{ fontSize: 14, color: "primary.main" }} />}
      label="ACH"
    />
    {cardPaymentEnabled && (
      <>
        <PaymentTypeDivider />
        <PaymentTypeLabel
          icon={<CreditCardIcon sx={{ fontSize: 14, color: "primary.main" }} />}
          label="Credit Card"
        />
        <PaymentTypeDivider />
        <PaymentTypeLabel
          icon={<CreditCardIcon sx={{ fontSize: 14, color: "primary.main" }} />}
          label="Debit Card"
        />
      </>
    )}
  </Box>
);

export const CheckoutPaymentMethodBox: React.FunctionComponent<CheckoutPaymentMethodBoxProps> = ({
  achAccount,
  cardAccount,
  cardPaymentEnabled,
  paymentChoice,
  onPaymentChoiceChange,
  action,
}) => {
  const controlWidthRef = useRef<HTMLDivElement>(null);
  const [controlWidth, setControlWidth] = useState<number | null>(null);

  const showAch = Boolean(achAccount);
  const showSavedCard = cardPaymentEnabled && Boolean(cardAccount);
  const showOneOff = cardPaymentEnabled;

  useLayoutEffect(() => {
    const element = controlWidthRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => {
      setControlWidth(element.offsetWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [showAch, showSavedCard, showOneOff, paymentChoice]);

  const handleChange = (event: SelectChangeEvent<CheckoutPaymentChoice>) => {
    onPaymentChoiceChange(event.target.value as CheckoutPaymentChoice);
  };

  const renderSelectedValue = (value: CheckoutPaymentChoice) => {
    if (value === "one_off") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <IconSlot>
            <CreditCardIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          </IconSlot>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            Pay With New Card
          </Typography>
        </Box>
      );
    }

    if (value === "saved_card") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <IconSlot>
            <CardBrandIcon brand={cardAccount?.bankName} compact />
          </IconSlot>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              Saved Card
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {formatCardDetail(cardAccount)}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <IconSlot>
          <AccountBalanceIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        </IconSlot>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            Saved Bank Account
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
            {formatAchDetail(achAccount)}
          </Typography>
        </Box>
      </Box>
    );
  };

  const resolvedControlWidth = controlWidth ?? undefined;
  // Two-line selected values need a bit more height
  const selectHeight =
    paymentChoice === "one_off" ? PAYMENT_CONTROL_HEIGHT : PAYMENT_CONTROL_HEIGHT + 10;

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "inline-flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 280,
      }}
    >
      <Box sx={{ width: "fit-content", maxWidth: "100%", minWidth: 280 }}>
        <PaymentMethodTypesRow cardPaymentEnabled={cardPaymentEnabled} />
        <PaymentSectionDivider />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <Box ref={controlWidthRef} sx={{ width: "fit-content", maxWidth: "100%", minWidth: 280 }}>
          <FormControl
            size="small"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: selectHeight,
                bgcolor: "grey.50",
              },
            }}
          >
            <InputLabel id="checkout-payment-method-label">Payment Method</InputLabel>
            <Select
              labelId="checkout-payment-method-label"
              id="checkout-payment-method"
              value={paymentChoice}
              label="Payment Method"
              onChange={handleChange}
              renderValue={renderSelectedValue}
            >
              {showAch && (
                <MenuItem value="ach">
                  <OptionRow
                    icon={
                      <AccountBalanceIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                    }
                    title="Saved Bank Account"
                    detail={formatAchDetail(achAccount)}
                  />
                </MenuItem>
              )}
              {showSavedCard && (
                <MenuItem value="saved_card">
                  <OptionRow
                    icon={<CardBrandIcon brand={cardAccount?.bankName} compact />}
                    title="Saved Card"
                    detail={formatCardDetail(cardAccount)}
                  />
                </MenuItem>
              )}
              {showOneOff && (
                <MenuItem value="one_off">
                  <OptionRow
                    icon={<CreditCardIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
                    title="Pay With New Card"
                  />
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        {action && (
          <Box
            sx={{
              width: resolvedControlWidth,
              minWidth: resolvedControlWidth,
              height: selectHeight,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            {action}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CheckoutPaymentMethodBox;
